import React, { useState } from 'react';
import {
  IconButton, Select, MenuItem, Box, Typography, Tooltip, Modal, Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Circle as CircleIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Cake as CakeIcon,
  WorkHistory as WorkHistoryIcon,
} from '@mui/icons-material';

import customLocaleText from '../../constants/DataGridLocaleTextConstants.js';
import moment from 'moment';
import { useAuth } from '../../context/AuthContext.js';

function TemplatesDataGrid({ templates, handleOpenDeleteModal, handleOpenModal }) {
  const [paginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState(null);
  const { user } = useAuth();

  const handleOpenPreviewModal = (template) => {
    setSelectedPreviewTemplate(template);
  };

  const handleClosePreviewModal = () => {
    setSelectedPreviewTemplate(null);
  };

  const getDateFromTimestamp = (timestamp) => {
    if (!timestamp) return 'Fecha inválida';
    return moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss');
  };

  // Configuración de tipos de template
  const getTemplateTypeConfig = (templateType, language) => {
    const configs = {
      normal: {
        label: 'Normal',
        color: 'default',
        icon: null
      },
      birthday: {
        label: 'Cumpleaños',
        color: 'secondary',
        icon: <CakeIcon sx={{ fontSize: 16 }} />
      },
      anniversary: {
        label: 'Aniversario',
        color: 'info',
        icon: <WorkHistoryIcon sx={{ fontSize: 16 }} />
      }
    };

    const config = configs[templateType] || configs.normal;
    const langLabel = language === 'es' ? 'ES' : language === 'pt' ? 'PT' : '';
    
    return {
      ...config,
      fullLabel: `${config.label}${langLabel ? ` - ${langLabel}` : ''}`
    };
  };

  const customFilterOperators = [
    {
      label: 'Activo/Inactivo',
      value: 'isActiveFilter',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        const isActive = filterItem.value === 'Activo';
        return (row) => (row === isActive);
      },
      InputComponent: ({ item, applyValue }) => (
        <Select
          value={item.value || ''}
          onChange={(event) => applyValue({ ...item, value: event.target.value })}
          style={{ width: '100%', marginTop: 'auto' }}
          data-testid="customStatusSelect"
          id="customStatusSelect"
        >
          <MenuItem value="">
            <em>--Seleccione--</em>
          </MenuItem>
          <MenuItem value={"Activo"}>Activo</MenuItem>
          <MenuItem value={"Inactivo"}>Inactivo</MenuItem>
        </Select>
      ),
    },
  ];

  const columns = [
    {
      display: 'flex',
      field: 'is_active',
      headerName: 'Estado',
      flex: 0.08,
      filterable: true,
      filterOperators: customFilterOperators,
      renderCell: (params) => (
        <CircleIcon
          sx={{
            color: params.row.is_active ? 'green' : 'grey',
          }}
        />
      ),
    },
    { 
      field: 'template_name', 
      headerName: 'Nombre', 
      flex: 0.22, 
      filterable: true 
    },
    { 
      field: 'description', 
      headerName: 'Descripción', 
      flex: 0.22, 
      filterable: true 
    },
    {
      field: 'template_type',
      headerName: 'Tipo',
      flex: 0.15,
      filterable: false,
      renderCell: (params) => {
        const templateType = params.row.template_type || 'normal';
        const language = params.row.language;
        const config = getTemplateTypeConfig(templateType, language);
        
        return (
          <Chip 
            icon={config.icon}
            label={config.fullLabel}
            color={config.color}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Fecha de creación',
      valueGetter: (params) => getDateFromTimestamp(params?._seconds),
      flex: 0.15,
      filterable: true,
    },
    {
      field: 'updated_at',
      headerName: 'Fecha ultima edición',
      valueGetter: (params) => getDateFromTimestamp(params?._seconds),
      flex: 0.15,
      filterable: true,
    },
    {
      filterable: false,
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.13,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => {
        return (
          <>
            <IconButton
              title='Vista previa'
              onClick={() => handleOpenPreviewModal(params.row)}
              aria-label="preview"
            >
              <VisibilityIcon />
            </IconButton>

            {user?.isAdmin && (
              <>
                <Tooltip title="Editar">
                  <IconButton
                    onClick={() => handleOpenModal(params.row)}
                    aria-label="edit"
                    data-testid="EditIcon"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Eliminar">
                  <IconButton
                    onClick={() => handleOpenDeleteModal(params.row)}
                    aria-label="delete"
                    data-testid="DeleteIcon"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    }
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '600px',
      }}
    >
      <DataGrid
        rows={templates}
        columns={columns}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[5, 10, 15, 20]}
        checkboxSelection={false}
        sx={{ border: 0 }}
        localeText={customLocaleText}
      />
      {selectedPreviewTemplate && (
        <Modal
          open={selectedPreviewTemplate}
          onClose={handleClosePreviewModal}
          aria-labelledby="preview-modal-title"
          aria-describedby="preview-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <Typography id="preview-modal-title" variant="h6" sx={{ mb: 2 }}>
              Vista Previa de la firma
            </Typography>
            <Box
              id="preview-modal-description"
              dangerouslySetInnerHTML={{ __html: selectedPreviewTemplate.template_html }}
              sx={{
                border: '1px solid #ddd',
                padding: 2,
                overflow: 'auto',
                maxWidth: '800px',
                width: 'auto',
                maxHeight: '100%',
              }}
            />
          </Box>
        </Modal>
      )}
    </div>
  );
}

export default TemplatesDataGrid;